import React, { useEffect, useState } from 'react'
import Select from 'react-select'
import ReactApexChart from 'react-apexcharts'

import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'
import ModalCall from 'pages/admin/posCare/ModalCall'
import ModalPosCard from 'pages/admin/posCare/ModalPosCard'
import Results from 'pages/admin/posCare/Results'
import { PROBLEM_TITLES } from 'pages/admin/setupHealth/checkTitles'

import { httpOk, GET, PUT } from 'helpers/api'
import { formatDate, formatMoney, returnSign } from 'helpers/helpers'

// Забота о клиентах: кто уходит молча.
//
// Точка перестаёт пробивать чеки задолго до того, как скажет «мы решили не
// продолжать». Экран ставит рядом две даты — последний чек и последний разговор —
// и сортирует список по первой из них: наверху те, кто не работал в программе
// дольше всех. Ради этого порядка страницу и открывают.
//
// Сверху сводка за период, снизу список. Считаются они разными запросами: список
// листается, сводка — нет, и перечитывать её на каждой странице незачем.
//
// Текст по-русски и без t(): админка не переводится (правило проекта).

const DIRECTION_TITLES = {
	out: 'Мы звонили',
	in: 'Нам звонили',
}

// Корзины — кому звонить и почему. Названия и пояснения приходят с бэка
// (CareBucketService::BUCKETS), здесь только цвет метки: деньги — красным,
// «не работают» — оранжевым, настройка — серым
// Вкладки страницы: работа оператора, сводка обзвона, результаты для руководителя
const PAGE_TABS = [
	['calls', 'Обзвон'],
	['summary', 'Сводка'],
	['results', 'Результаты'],
]

const BUCKET_CLASSES = {
	callback_due: 'bg-info text-dark',
	unpaid: 'bg-danger',
	lost_paid: 'bg-danger',
	low_balance: 'bg-danger bg-opacity-75',
	new_not_started: 'bg-primary',
	lost_unpaid: 'bg-danger bg-opacity-75',
	idle: 'bg-warning text-dark',
	intermittent: 'bg-warning text-dark',
	dropping: 'bg-warning text-dark',
	churned: 'bg-dark',
	setup_problems: 'bg-secondary',
}

// Сортировка: ключ колонки уходит на бэк, там он сверяется с белым списком —
// в ORDER BY пользовательский текст не попадает
function getDefaultFilter() {
	const date = new Date()

	return {
		'search': '',
		'bucket': null,
		'show_in_work': false,
		'operator': null,
		'region_id': null,
		'pos_type_id': null,
		'only_overdue': false,
		'only_silent': false,
		'tariff_not_zero': false,
		'contact_days': 3,
		'order_by': 'cheque,asc',
		'page': 0,
		'start_date': formatDate(new Date(date.getFullYear(), date.getMonth(), date.getDate() - 13), 'yyyy-MM-dd'),
		'end_date': formatDate(date, 'yyyy-MM-dd'),
	}
}

function Index() {
	const [filterData, setFilterData] = useState(getDefaultFilter())
	const [dashboard, setDashboard] = useState(null)
	const [data, setData] = useState({ 'data': [], 'total': 0 })
	const [pagination, setPagination] = useState({ 'url': '/admin/pos-care', 'response': null, 'size': 20 })

	const [regions, setRegions] = useState([])
	const [posTypes, setPosTypes] = useState([])
	const [topics, setTopics] = useState([])
	const [operators, setOperators] = useState([])

	// «Обзвон» — работа оператора на сегодня, «Результаты» — для руководителя
	const [tab, setTab] = useState('calls')

	// Отмеченные строки — для закрепления пачкой за оператором
	const [checkedIds, setCheckedIds] = useState([])
	const [assignTo, setAssignTo] = useState(null)

	const [selectedPos, setSelectedPos] = useState(null)
	const [modalCall, setModalCall] = useState(false)
	const [modalPosCard, setModalPosCard] = useState(false)

	function buildUrl(base, nextFilterData, withPage = true) {
		var urlParams = ''

		if (nextFilterData.search)
			urlParams += returnSign(urlParams) + 'search=' + encodeURIComponent(nextFilterData.search)
		if (nextFilterData.bucket)
			urlParams += returnSign(urlParams) + 'bucket=' + nextFilterData.bucket
		if (nextFilterData.bucket && nextFilterData.show_in_work)
			urlParams += returnSign(urlParams) + 'show_in_work=true'
		if (nextFilterData.operator)
			urlParams += returnSign(urlParams) + 'operator=' + nextFilterData.operator
		if (nextFilterData.region_id)
			urlParams += returnSign(urlParams) + 'region_id=' + nextFilterData.region_id
		if (nextFilterData.pos_type_id)
			urlParams += returnSign(urlParams) + 'pos_type_id=' + nextFilterData.pos_type_id
		if (nextFilterData.only_overdue)
			urlParams += returnSign(urlParams) + 'only_overdue=true'
		if (nextFilterData.only_silent)
			urlParams += returnSign(urlParams) + 'only_silent=true'
		if (nextFilterData.tariff_not_zero)
			urlParams += returnSign(urlParams) + 'tariff_not_zero=true'
		if (nextFilterData.contact_days)
			urlParams += returnSign(urlParams) + 'contact_days=' + nextFilterData.contact_days
		if (nextFilterData.order_by)
			urlParams += returnSign(urlParams) + 'order_by=' + nextFilterData.order_by
		if (withPage && nextFilterData.page)
			urlParams += returnSign(urlParams) + 'page=' + nextFilterData.page

		return base + urlParams
	}

	async function getDashboard(nextFilterData = filterData) {
		var urlParams = ''
		urlParams += returnSign(urlParams) + 'start_date=' + nextFilterData.start_date
		urlParams += returnSign(urlParams) + 'end_date=' + nextFilterData.end_date
		urlParams += returnSign(urlParams) + 'contact_days=' + nextFilterData.contact_days
		if (nextFilterData.operator)
			urlParams += returnSign(urlParams) + 'operator=' + nextFilterData.operator

		const response = await GET('/admin/pos-care/dashboard' + urlParams, {}, { loader: true })
		if (httpOk(response)) setDashboard(response.data)
	}

	async function searchWithFilter(nextFilterData = filterData) {
		const url = buildUrl('/admin/pos-care', nextFilterData)

		const response = await GET(url, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': url, 'response': response.data })
		}
	}

	async function getHelpers() {
		const regionsResponse = await GET('/admin/pos-care/regions', {}, { loader: false })
		if (httpOk(regionsResponse)) setRegions(regionsResponse.data || [])

		const posTypesResponse = await GET('/admin/helper/pos-types', {}, { loader: false })
		if (httpOk(posTypesResponse)) setPosTypes(posTypesResponse.data || [])

		const topicsResponse = await GET('/admin/helper/call-topics', {}, { loader: false })
		if (httpOk(topicsResponse)) setTopics(topicsResponse.data || [])

		getOperators()
	}

	async function getOperators() {
		const response = await GET('/admin/pos-care/operators', {}, { loader: false })
		if (httpOk(response)) setOperators(response.data || [])
	}

	// Закрепить отмеченные точки (operatorId = null — снять закрепление)
	async function assignChecked(operatorId) {
		if (checkedIds.length === 0) return

		const response = await PUT('/admin/pos-care/assign', { 'pos_ids': checkedIds, 'user_id': operatorId }, { loader: true })
		if (httpOk(response)) {
			setCheckedIds([])
			setAssignTo(null)
			searchWithFilter()
			getDashboard()
			getOperators()
		}
	}

	function toggleChecked(id) {
		setCheckedIds(checkedIds.includes(id) ? checkedIds.filter((item) => item !== id) : [...checkedIds, id])
	}

	const pageIds = data.data.map((row) => row.id)
	const allPageChecked = pageIds.length > 0 && pageIds.every((id) => checkedIds.includes(id))

	function toggleAllPage() {
		setCheckedIds(allPageChecked
			? checkedIds.filter((id) => !pageIds.includes(id))
			: [...new Set([...checkedIds, ...pageIds])])
	}

	// «Чьи точки»: свои, ничьи или конкретного оператора
	const operatorOptions = [
		{ 'id': 'me', 'name': 'Мои точки' },
		{ 'id': 'none', 'name': 'Не закреплены' },
		...operators.map((item) => ({ 'id': String(item.id), 'name': item.name + ' (' + item.assigned_count + ')' })),
	]

	// Тип точки: по умолчанию (пусто) — все, кроме складов; all — со складами; id — конкретный тип
	const posTypeOptions = [
		{ 'id': 'all', 'name': 'Все типы, со складами' },
		...posTypes.map((item) => ({ 'id': String(item.id), 'name': item.name })),
	]

	function applyFilter(patch, reloadDashboard = false) {
		const nextFilterData = { ...filterData, ...patch, 'page': 0 }
		setCheckedIds([])
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
		if (reloadDashboard) getDashboard(nextFilterData)
	}

	function handlePaginate(page) {
		const nextFilterData = { ...filterData, page }
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
	}

	// Клик по заголовку: первый раз — по возрастанию, повторный — переворот
	function handleSort(column) {
		const [currentColumn, currentDirection] = filterData.order_by.split(',')
		const direction = currentColumn === column && currentDirection === 'asc' ? 'desc' : 'asc'

		applyFilter({ 'order_by': column + ',' + direction })
	}

	function sortIcon(column) {
		const [currentColumn, currentDirection] = filterData.order_by.split(',')
		if (currentColumn !== column) return 'uil-angle-down text-muted'

		return currentDirection === 'asc' ? 'uil-angle-up' : 'uil-angle-down'
	}

	function toggleModalCall(bool = false, pos = null) {
		setSelectedPos(pos)
		setModalCall(bool)
	}

	function toggleModalPosCard(bool = false, pos = null) {
		setSelectedPos(pos)
		setModalPosCard(bool)
	}

	function handleChanged() {
		searchWithFilter()
		getDashboard()
	}

	function handleResetFilters() {
		const nextFilterData = getDefaultFilter()
		setFilterData(nextFilterData)
		searchWithFilter(nextFilterData)
		getDashboard(nextFilterData)
	}

	// Вкладка-корзина (null — все точки). В корзине порядок — по важности (самая
	// важная причина, внутри — тариф), без корзины — прежний, по последнему чеку
	function selectBucket(code) {
		const bucket = code || null
		applyFilter({ 'bucket': bucket, 'order_by': bucket ? 'priority,desc' : 'cheque,asc' })
	}

	// Плитки «Сводки», где цифра — работа на сегодня, открывают список с этим фильтром
	function openCallsWith(patch) {
		setTab('calls')
		applyFilter({ 'bucket': null, 'order_by': 'cheque,asc', ...patch })
	}

	const bucketTitles = Object.fromEntries((dashboard?.bucket_catalog || []).map((item) => [item.code, item.title]))

	const bucketTabs = dashboard?.buckets
		? [
			{ 'code': null, 'title': 'Все точки', 'count': dashboard.total_poses, 'hint': 'Действующие точки без складов, порядок — по последнему чеку' },
			{ 'code': 'any', 'title': 'Нужен звонок', 'count': dashboard.buckets.any, 'hint': 'Хотя бы одна причина позвонить; самые важные — сверху' },
			...(dashboard.bucket_catalog || []).map((item) => ({ ...item, 'count': dashboard.buckets[item.code] })),
		]
		: []
	const activeBucketHint = bucketTabs.find((item) => item.code === (filterData.bucket || null))?.hint

	// Сколько в выбранной корзине точек «в работе» — им уже позвонили, в счётчик они не идут.
	// У «Перезвонить сегодня» такого нет: это и есть работа оператора на сегодня
	const inWorkCount = filterData.bucket && filterData.bucket !== 'callback_due'
		? Number(dashboard?.buckets?.in_work?.[filterData.bucket] || 0)
		: 0

	// Почему точка «в работе» — что с ней уже договорились
	function inWorkReason(row) {
		const today = formatDate(new Date(), 'yyyy-MM-dd')
		if (row.last_outcome_code === 'promised_pay' && row.promised_pay_at && row.promised_pay_at > today) {
			return 'обещали оплатить до ' + formatDate(row.promised_pay_at, 'dd.MM')
		}
		if (row.next_call_at && row.next_call_at > today) {
			return 'перезвон ' + formatDate(row.next_call_at, 'dd.MM')
		}
		if (row.last_answered_at) {
			return 'говорили ' + formatDate(row.last_answered_at, 'dd.MM')
		}
		return null
	}

	// Что именно сказать клиенту — цифра к каждой причине звонка
	function bucketDetails(row) {
		const details = []
		const buckets = row.buckets || []

		if (buckets.includes('callback_due')) {
			if (row.last_outcome_code === 'promised_pay' && row.promised_pay_at) {
				details.push('обещали оплатить до ' + formatDate(row.promised_pay_at, 'dd.MM')
					+ (row.promised_amount ? ' · ' + formatMoney(row.promised_amount) : ''))
			}
			if (row.next_call_at) details.push('перезвон на ' + formatDate(row.next_call_at, 'dd.MM'))
		}

		if (Number(row.balance) < 0 && (buckets.includes('unpaid') || buckets.includes('low_balance'))) {
			details.push('долг ' + formatMoney(-Number(row.balance)))
		}
		if (buckets.includes('low_balance')) {
			if (row.access_until) details.push('доступ до ' + formatDate(row.access_until, 'dd.MM'))
			else if (Number(row.balance) >= 0) details.push('не хватает на 1-е ' + formatMoney(row.shortfall))
		}
		if (buckets.includes('lost_paid') || buckets.includes('lost_unpaid')) {
			details.push('молчит ' + row.days_without_cheque + ' дн.'
				+ (Number(row.paid_month) > 0 ? ', в этом месяце внесли ' + formatMoney(row.paid_month) : ', в этом месяце не платили'))
		}
		if (buckets.includes('new_not_started')) {
			details.push('подключена ' + formatDate(row.created_at, 'dd.MM') + ', работала дней: ' + row.days_worked)
		}
		if (buckets.includes('intermittent')) {
			details.push('за 14 дней работала ' + row.worked_14d + ' дн. (раньше ' + row.worked_prev_28d + ' из 28)'
				+ (row.max_gap_14d > 0 ? ', перерыв до ' + row.max_gap_14d + ' дн.' : ''))
		}
		if (buckets.includes('dropping')) {
			details.push('чеков за неделю ' + row.cheques_7d + ', обычно ' + row.cheques_prev_week_avg)
		}
		if (buckets.includes('setup_problems')) {
			(row.setup_problem_codes || []).forEach((code) => details.push(PROBLEM_TITLES[code] || code))
		}

		return details
	}

	// Дней без контакта — красным, когда просрочка: по этой колонке и выбирают,
	// кому звонить сегодня
	function contactClass(row) {
		if (row.days_without_contact === null) return 'text-danger'
		if (row.days_without_contact > filterData.contact_days) return 'text-warning'

		return ''
	}

	const callsChart = {
		options: {
			chart: { type: 'bar', stacked: false, toolbar: { show: false }, fontFamily: 'inherit' },
			colors: ['#4c8df0', '#9b5de5'],
			plotOptions: { bar: { columnWidth: '45%', borderRadius: 3 } },
			dataLabels: { enabled: false },
			legend: { position: 'bottom' },
			xaxis: {
				categories: (dashboard?.calls_by_day || []).map((item) => formatDate(item.date, 'dd.MM')),
			},
			yaxis: { labels: { formatter: (value) => Math.round(value) } },
		},
		series: [
			{ name: 'Мы звонили', data: (dashboard?.calls_by_day || []).map((item) => Number(item.out_count)) },
			{ name: 'Нам звонили', data: (dashboard?.calls_by_day || []).map((item) => Number(item.in_count)) },
		],
	}

	const contactAgeChart = {
		options: {
			chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
			colors: ['#1f9d57'],
			plotOptions: { bar: { columnWidth: '45%', borderRadius: 3, distributed: true } },
			dataLabels: { enabled: true },
			legend: { show: false },
			xaxis: { categories: ['0–3 дня', '4–7 дней', '8–14 дней', '15+ дней', 'Не говорили'] },
		},
		series: [{
			name: 'Точек',
			data: [
				Number(dashboard?.contact_age?.d0_3 || 0),
				Number(dashboard?.contact_age?.d4_7 || 0),
				Number(dashboard?.contact_age?.d8_14 || 0),
				Number(dashboard?.contact_age?.d15_plus || 0),
				Number(dashboard?.contact_age?.never || 0),
			],
		}],
	}

	useEffect(() => {
		getHelpers()
		getDashboard()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
				<div className="vertical-center">
					<h4 className="mb-0">Забота о клиентах</h4>
					<div className="text-muted fs-6">
						Кто уходит молча: точка перестаёт пробивать чеки задолго до того, как скажет «мы решили не продолжать»
					</div>
				</div>
				<div className="btn-group">
					{PAGE_TABS.map(([id, title]) => (
						<button type="button" key={id}
							className={`btn ${tab === id ? 'btn-primary' : 'btn-outline-primary'}`}
							onClick={() => setTab(id)}>
							{title}
						</button>
					))}
				</div>
			</div>

			{tab === 'results' && <Results />}

			{/* Сводка обзвона — посмотреть, как идёт работа, а не работать со списком: поэтому
				отдельной вкладкой, иначе плитки и графики отодвигают таблицу на экран вниз */}
			{tab === 'summary' && <>
				<div className="card mb-3">
					<div className="card-body py-2 d-flex flex-wrap align-items-end gap-3">
						<div style={{ 'width': '340px' }}>
							<label className="fs-6 text-muted">Период</label>
							<PeriodPicker
								startDate={filterData.start_date}
								endDate={filterData.end_date}
								onChange={(period) => applyFilter(period, true)}
								withTime={false}
							/>
						</div>
					</div>
				</div>

				{/* Плитки, где цифра — работа на сегодня, ведут в список с этим фильтром */}
				{dashboard &&
					<div className="row">
						<div className="col-xl-3 col-md-4 col-6 mb-3">
							<div className="card h-100 mb-0 cursor-pointer"
								onClick={() => openCallsWith({ 'only_overdue': true })}>
								<div className="card-body p-2 text-center">
									<div className="fw-bold fs-4 text-danger">{dashboard.overdue}</div>
									<div className="fs-6">Просрочен обзвон</div>
									<div className="fs-6 text-muted">не говорили больше {dashboard.contact_days} дн.</div>
								</div>
							</div>
						</div>

						<div className="col-xl-3 col-md-4 col-6 mb-3">
							<div className="card h-100 mb-0 cursor-pointer"
								onClick={() => openCallsWith({ 'only_silent': true })}>
								<div className="card-body p-2 text-center">
									<div className="fw-bold fs-4 text-warning">{dashboard.silent_pos}</div>
									<div className="fs-6">Молчит касса</div>
									<div className="fs-6 text-muted">из них 7+ дней: {dashboard.silent_pos_week}</div>
								</div>
							</div>
						</div>

						<div className="col-xl-3 col-md-4 col-6 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body p-2 text-center">
									<div className="fw-bold fs-4">{dashboard.never_called}</div>
									<div className="fs-6">Ни разу не звонили</div>
									<div className="fs-6 text-muted">работа онбординга, а не обзвона</div>
								</div>
							</div>
						</div>

						<div className="col-xl-3 col-md-4 col-6 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body p-2 text-center">
									<div className="fw-bold fs-4 text-success">{dashboard.called_today}</div>
									<div className="fs-6">Обзвонено сегодня</div>
									<div className="fs-6 text-muted">осталось в очереди: {dashboard.overdue}</div>
								</div>
							</div>
						</div>

						<div className="col-xl-3 col-md-4 col-6 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body p-2 text-center">
									<div className="fw-bold fs-4">{dashboard.calls_total}</div>
									<div className="fs-6">Звонков за период</div>
									<div className="fs-6 text-muted">
										мы {dashboard.calls_out} · нам {dashboard.calls_in}
									</div>
								</div>
							</div>
						</div>

						<div className="col-xl-3 col-md-4 col-6 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body p-2 text-center">
									<div className="fw-bold fs-4">{dashboard.answer_rate}%</div>
									<div className="fs-6">Доля дозвона</div>
									<div className="fs-6 text-muted">по исходящим</div>
								</div>
							</div>
						</div>

						<div className="col-xl-3 col-md-4 col-6 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body p-2 text-center">
									<div className={`fw-bold fs-4 ${dashboard.problems > 0 ? 'text-danger' : ''}`}>
										{dashboard.problems}
									</div>
									<div className="fs-6">Найдено проблем</div>
									<div className="fs-6 text-muted">ради них и звоним</div>
								</div>
							</div>
						</div>

						<div className="col-xl-3 col-md-4 col-6 mb-3">
							<div className="card h-100 mb-0">
								<div className="card-body p-2 text-center">
									<div className="fw-bold fs-4">{dashboard.total_poses}</div>
									<div className="fs-6">Всего точек</div>
									<div className="fs-6 text-muted">задето за период: {dashboard.poses_touched}</div>
								</div>
							</div>
						</div>
					</div>
				}

				{dashboard &&
					<div className="row mb-3">
						<div className="col-lg-7 mb-3 mb-lg-0">
							<div className="card h-100 mb-0">
								<div className="card-body">
									<h6 className="mb-0">Звонки по дням</h6>
									<ReactApexChart options={callsChart.options} series={callsChart.series} type="bar" height={280} />
								</div>
							</div>
						</div>
						<div className="col-lg-5">
							<div className="card h-100 mb-0">
								<div className="card-body">
									<h6 className="mb-0">
										Давность контакта
										<span className="ms-2 text-muted fs-6">видно, копится ли хвост</span>
									</h6>
									<ReactApexChart options={contactAgeChart.options} series={contactAgeChart.series} type="bar" height={280} />
								</div>
							</div>
						</div>
					</div>
				}
			</>}

			{tab === 'calls' && <>

			{/* Корзины — вкладками прямо над таблицей: щёлкнул — список под ними сразу
				сменился. Точка бывает в нескольких корзинах, счётчики не суммируются */}
			{dashboard?.buckets &&
				<div className="card mb-2">
					<div className="card-body py-2">
						<div className="d-flex flex-wrap gap-2">
							{bucketTabs.map((item) => {
								const active = (filterData.bucket || null) === item.code
								const count = item.count ?? 0

								return (
									<button type="button" key={item.code || 'all'} title={item.hint}
										className={`btn btn-sm d-inline-flex align-items-center gap-2 ${active ? 'btn-primary' : 'btn-light border'} ${!active && count === 0 ? 'opacity-50' : ''}`}
										onClick={() => selectBucket(item.code)}>
										{BUCKET_CLASSES[item.code] &&
											<span className={`d-inline-block rounded-circle ${BUCKET_CLASSES[item.code].split(' ')[0]}`}
												style={{ 'width': '8px', 'height': '8px' }}></span>
										}
										<span>{item.title}</span>
										<span className={`badge rounded-pill ${active ? 'bg-white text-primary' : 'bg-secondary'}`}>{count}</span>
									</button>
								)
							})}
						</div>
						<div className="d-flex flex-wrap align-items-center gap-3 mt-2">
							{activeBucketHint && <span className="text-muted fs-6">{activeBucketHint}</span>}

							{/* Кому уже позвонили — не в счётчике и скрыты, пока не наступит перезвон
								или не пройдёт «звонить раз в N дн.» без нового разговора */}
							{inWorkCount > 0 &&
								<div className="form-check form-switch mb-0">
									<input type="checkbox" className="form-check-input" id="showInWork"
										checked={filterData.show_in_work}
										onChange={(e) => applyFilter({ 'show_in_work': e.target.checked })} />
									<label className="form-check-label fs-6" htmlFor="showInWork">
										Показать в работе ({inWorkCount}) — им уже позвонили
									</label>
								</div>
							}
						</div>
					</div>
				</div>
			}

			{/* Фильтры списка */}
			<div className="card mb-2">
				<div className="card-body py-2 d-flex flex-wrap align-items-center gap-2">
					<div style={{ 'width': '240px' }}>
						<input type="text" className="form-control"
							placeholder="Точка, логин, телефон или ID"
							value={filterData.search}
							onChange={(e) => setFilterData({ ...filterData, 'search': e.target.value })}
							onKeyUp={(e) => {
								if (e.key === 'Enter') applyFilter({ 'search': filterData.search })
							}} />
					</div>

					<div style={{ 'width': '200px' }}>
						<Select
							isClearable
							options={operatorOptions}
							value={operatorOptions.find((item) => item.id === filterData.operator) || ''}
							onChange={(option) => applyFilter({ 'operator': option ? option.id : null }, true)}
							placeholder="Чьи точки: все"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>

					<div style={{ 'width': '200px' }}>
						<Select
							isClearable
							options={regions}
							value={regions.find((item) => item.id === filterData.region_id) || ''}
							onChange={(option) => applyFilter({ 'region_id': option ? option.id : null })}
							placeholder="Регион"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>

					{/* Склады по умолчанию скрыты: чеков у них нет, и по «последнему чеку»
						они всегда наверху как неработающие */}
					<div style={{ 'width': '220px' }}>
						<Select
							isClearable
							options={posTypeOptions}
							value={posTypeOptions.find((item) => item.id === filterData.pos_type_id) || ''}
							onChange={(option) => applyFilter({ 'pos_type_id': option ? option.id : null })}
							placeholder="Тип точки: без складов"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
						/>
					</div>

					<div className="form-check form-switch mb-0">
						<input type="checkbox" className="form-check-input" id="onlyOverdue"
							checked={filterData.only_overdue}
							onChange={(e) => applyFilter({ 'only_overdue': e.target.checked })} />
						<label className="form-check-label" htmlFor="onlyOverdue">Просрочен обзвон</label>
					</div>

					<div className="form-check form-switch mb-0">
						<input type="checkbox" className="form-check-input" id="onlySilent"
							checked={filterData.only_silent}
							onChange={(e) => applyFilter({ 'only_silent': e.target.checked })} />
						<label className="form-check-label" htmlFor="onlySilent">Молчит касса</label>
					</div>

					<div className="form-check form-switch mb-0">
						<input type="checkbox" className="form-check-input" id="tariffNotZero"
							checked={filterData.tariff_not_zero}
							onChange={(e) => applyFilter({ 'tariff_not_zero': e.target.checked })} />
						<label className="form-check-label" htmlFor="tariffNotZero">Без тестовых</label>
					</div>

					{/* Порог «просрочен обзвон» и «молчит касса» — рядом с переключателями, которые от него зависят */}
					<div className="d-flex align-items-center gap-1" title="Через сколько дней без разговора обзвон считается просроченным">
						<span className="text-muted fs-6">звонить раз в</span>
						<input type="number" min="1" max="90" className="form-control form-control-sm" style={{ 'width': '60px' }}
							value={filterData.contact_days}
							onChange={(e) => setFilterData({ ...filterData, 'contact_days': e.target.value })}
							onBlur={() => applyFilter({ 'contact_days': Number(filterData.contact_days) || 3 }, true)} />
						<span className="text-muted fs-6">дн.</span>
					</div>

					<div className="ms-auto d-flex align-items-center gap-2">
						<span className="text-muted fs-6">Найдено точек: {data.total}</span>
						<button className="btn btn-sm btn-outline-secondary" onClick={() => handleResetFilters()}>
							Сбросить
						</button>
					</div>
				</div>
			</div>

			{/* Закрепление пачкой: отметили строки — отдали оператору целиком */}
			{checkedIds.length > 0 &&
				<div className="card mb-3 border-primary">
					<div className="card-body py-2 d-flex flex-wrap align-items-center gap-2">
						<span>Отмечено точек: <b>{checkedIds.length}</b></span>
						<div style={{ 'width': '240px' }}>
							<Select
								options={operators}
								value={operators.find((item) => item.id === assignTo) || ''}
								onChange={(option) => setAssignTo(option ? option.id : null)}
								placeholder="Закрепить за…"
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
							/>
						</div>
						<button className="btn btn-primary" disabled={!assignTo} onClick={() => assignChecked(assignTo)}>
							Закрепить
						</button>
						<button className="btn btn-outline-danger" onClick={() => assignChecked(null)}>
							Снять закрепление
						</button>
						<button className="btn btn-link ms-auto" onClick={() => setCheckedIds([])}>
							Сбросить отметки
						</button>
					</div>
				</div>
			}

			<div className="card">
				<div className="card-body">
					<table className="table table-bordered table-hover mb-0">
						<thead>
							<tr>
								<th style={{ 'width': '36px' }} className="text-center">
									<input type="checkbox" className="form-check-input" checked={allPageChecked} onChange={toggleAllPage} />
								</th>
								<th className="cursor-pointer" onClick={() => handleSort('name')}>
									Точка <i className={`uil ${sortIcon('name')}`}></i>
								</th>
								<th className="cursor-pointer" style={{ 'minWidth': '220px' }} onClick={() => handleSort('priority')}>
									Почему звонить <i className={`uil ${sortIcon('priority')}`}></i>
								</th>
								<th>Город</th>
								<th className="text-end cursor-pointer" onClick={() => handleSort('balance')}>
									Баланс <i className={`uil ${sortIcon('balance')}`}></i>
								</th>
								<th className="cursor-pointer" onClick={() => handleSort('cheque')}>
									Последний чек <i className={`uil ${sortIcon('cheque')}`}></i>
								</th>
								<th className="text-center">Чеков за 7 дней</th>
								<th className="cursor-pointer" onClick={() => handleSort('contact')}>
									Последний разговор <i className={`uil ${sortIcon('contact')}`}></i>
								</th>
								<th className="text-center">Дней без контакта</th>
								<th style={{ 'width': '150px' }}></th>
							</tr>
						</thead>
						<tbody>
							{data.data.map((row) => (
								<tr key={row.id}>
									<td className="text-center">
										<input type="checkbox" className="form-check-input"
											checked={checkedIds.includes(row.id)}
											onChange={() => toggleChecked(row.id)} />
									</td>
									<td>
										<div>
											{row.name}
											{Number(row.status) === -1 && <span className="badge bg-danger ms-1">заблокирована</span>}
											{row.is_warehouse && <span className="badge bg-info text-dark ms-1">{row.pos_type_name}</span>}
										</div>
										<div className="text-muted fs-6">{row.login}</div>
										{/* Агент нужен, когда точку недообучили: задачу на обучение ставят ему */}
										{row.agent_name && <div className="text-muted fs-6">агент: {row.agent_name}</div>}
										{row.assigned_name
											? <div className="fs-6"><i className="uil uil-user me-1"></i>{row.assigned_name}</div>
											: <div className="text-muted fs-6">не закреплена</div>}
									</td>
									<td>
										{(row.buckets || []).length === 0 && <span className="text-muted">—</span>}
										{(row.buckets || []).map((code) => (
											<span key={code} className={`badge ${BUCKET_CLASSES[code] || 'bg-secondary'} me-1 mb-1`}>
												{bucketTitles[code] || code}
											</span>
										))}
										{row.in_work &&
											<div className="mt-1">
												<span className="badge bg-success me-1">в работе</span>
												<span className="text-muted fs-6">{inWorkReason(row)}</span>
											</div>
										}
										{bucketDetails(row).map((detail, index) => (
											<div key={index} className="text-muted fs-6">{detail}</div>
										))}
									</td>
									<td>
										<div>{row.district_name || '—'}</div>
										<div className="text-muted fs-6">{row.region_name || ''}</div>
									</td>
									<td className={`text-end ${Number(row.balance) < 0 ? 'text-danger' : ''}`}>
										{formatMoney(row.balance)}
									</td>
									{/* Склад чеков не пробивает — вместо чека его последний приход или перемещение */}
									{row.is_warehouse
										? <td>
											<div className="text-muted fs-6">документ</div>
											{row.last_document_date
												? <>
													<div>{formatDate(row.last_document_date, 'dd.MM.yyyy HH:mm')}</div>
													<div className="text-muted fs-6">{row.days_without_document} дн. назад</div>
												</>
												: <span className="text-danger">Никогда</span>}
										</td>
										: <td>
											{row.last_cheque_date
												? <>
													<div>{formatDate(row.last_cheque_date, 'dd.MM.yyyy HH:mm')}</div>
													<div className="text-muted fs-6">{row.days_without_cheque} дн. назад</div>
												</>
												: <span className="text-danger">Никогда</span>}
										</td>
									}
									<td className="text-center">
										{row.is_warehouse
											? <>{row.documents_week}<div className="text-muted fs-6">док.</div></>
											: row.cheques_week}
									</td>
									<td>
										{row.last_call_at
											? <>
												<div>
													{formatDate(row.last_call_at, 'dd.MM.yyyy HH:mm')}
													<span className="ms-1 text-muted fs-6">
														{DIRECTION_TITLES[row.last_call_direction]}
													</span>
												</div>
												<div className="text-muted fs-6">
													{[row.last_call_contact, row.last_call_topic, row.last_call_operator]
														.filter((item) => !!item).join(' · ')}
												</div>

												{/* О чём говорили — то, ради чего журнал и ведётся.
													Длинный комментарий режем: строка списка не должна
													разъезжаться на пол-экрана */}
												{row.last_call_comment &&
													<div className="text-muted fs-6" title={row.last_call_comment}>
														{row.last_call_comment.length > 90
															? row.last_call_comment.slice(0, 90) + '…'
															: row.last_call_comment}
													</div>
												}
											</>
											: <span className="text-danger">Не говорили ни разу</span>}
									</td>
									<td className={`text-center ${contactClass(row)}`}>
										{row.days_without_contact === null ? '—' : row.days_without_contact}
									</td>
									<td className="text-end">
										<button className="btn btn-sm btn-primary mb-1 w-100"
											onClick={() => toggleModalCall(true, row)}>
											<i className="uil uil-phone me-1"></i>
											Звонок
										</button>
										<button className="btn btn-sm btn-outline-primary w-100"
											onClick={() => toggleModalPosCard(true, row)}>
											Карточка точки
										</button>
									</td>
								</tr>
							))}

							{data.data.length === 0 &&
								<tr>
									<td colSpan="10" className="text-center text-muted">Точек по фильтру нет</td>
								</tr>
							}
						</tbody>
					</table>

					<FilterPagination
						pagination={pagination}
						paginate={handlePaginate} />
				</div>
			</div>
			</>}

			{/* MODAL — форма звонка по выбранной точке */}
			<ModalCall
				show={modalCall}
				onHide={() => toggleModalCall()}
				pos={selectedPos}
				topics={topics}
				onSaved={handleChanged}
			/>

			{/* MODAL — карточка точки: контакты и история разговоров */}
			<ModalPosCard
				show={modalPosCard}
				onHide={() => toggleModalPosCard()}
				pos={selectedPos}
				topics={topics}
				onChanged={handleChanged}
			/>
		</>
	)
}

export default Index
